import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { CreditCard, Check, Calendar, Clock, X as XIcon, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";

/**
 * Public page where a client lands from the "Accept & Pay" link in the
 * follow-up offer email. Polls the public status endpoint after Stripe returns.
 */
export default function FollowUpAccept() {
  const { token } = useParams();
  const [offer, setOffer] = useState(null);
  const [busy, setBusy] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");

  const load = () => {
    api.get(`/follow-up-requests/token/${token}`)
      .then((r) => setOffer(r.data))
      .catch(() => setOffer({ error: "Offer not found or expired" }));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paid = params.get("paid_session");
    if (!paid) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await api.get(`/payments/checkout/private-status/${paid}`);
        if (cancelled) return;
        if (r.data.payment_status === "paid") {
          setStatusMsg("paid");
          toast.success("Payment received — your session is locked in.");
          window.history.replaceState({}, "", window.location.pathname);
          load();
        } else if (r.data.payment_status === "initiated") {
          setTimeout(poll, 2000);
        }
      } catch (e) { /* ignore */ }
    };
    poll();
    return () => { cancelled = true; };
  }, [token]);

  const accept = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/follow-up-requests/token/${token}/accept`, {
        origin_url: window.location.origin,
      });
      if (r.data.url) {
        window.location.href = r.data.url;
      } else if (r.data.status === "accepted") {
        toast.success("Your session is locked in!");
        load();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't accept");
      setBusy(false);
    }
  };

  if (!offer) {
    return <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center text-stone-500">Loading…</div>;
  }
  if (offer.error) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
        <PublicHeader />
        <div className="flex-1 flex items-center justify-center p-10">
          <div className="card-pp p-10 text-center max-w-md">
            <XIcon size={32} className="mx-auto mb-3 text-red-400" />
            <h1 className="font-heading font-bold text-xl mb-2">Offer not available</h1>
            <p className="text-stone-600 text-sm">This link is invalid or has expired. Please ask the team to send a fresh offer.</p>
          </div>
        </div>
        <PublicFooter />
      </div>
    );
  }
  const when = (offer.starts_at || "").replace("T", " ").slice(0, 16);
  const isPaid = offer.status === "paid" || statusMsg === "paid";
  const isAccepted = offer.status === "accepted";
  const finalised = isPaid || isAccepted;
  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
      <PublicHeader />
      <main className="flex-1 px-6 lg:px-12 py-12 max-w-2xl mx-auto w-full">
        <div className="card-pp p-8" data-testid="follow-up-card">
          <div className="text-xs uppercase tracking-wider font-bold text-[#f38838] mb-2">Follow-up session offer</div>
          <h1 className="font-heading font-bold text-3xl text-stone-900 mb-4">{offer.service_title}</h1>
          {offer.message && <p className="text-stone-700 mb-4">{offer.message}</p>}
          <div className="space-y-2 text-sm border-y border-stone-200 py-4 my-2">
            <div className="flex items-center gap-2"><Calendar size={16} weight="duotone" className="text-[#1b4a76]" /> <strong>When:</strong> {when}</div>
            <div className="flex items-center gap-2"><Clock size={16} weight="duotone" className="text-[#1b4a76]" /> <strong>Duration:</strong> {offer.duration_min} minutes</div>
            <div className="flex items-center gap-2"><CreditCard size={16} weight="duotone" className="text-[#1b4a76]" /> <strong>Price:</strong> {offer.price_aud > 0 ? `$${offer.price_aud.toFixed(0)} AUD` : "No charge"}</div>
          </div>
          {finalised ? (
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-emerald-800 flex items-start gap-2" data-testid="follow-up-confirmed">
              <Check size={20} weight="fill" className="mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-semibold">You're locked in!</div>
                <div className="text-sm mt-0.5">A confirmation email is on its way. See you on {when}.</div>
              </div>
            </div>
          ) : offer.status === "declined" ? (
            <div className="rounded-xl bg-stone-100 p-4 text-stone-600 text-center">This offer was withdrawn.</div>
          ) : (
            <button onClick={accept} disabled={busy} className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="follow-up-accept">
              {offer.price_aud > 0 ? <CreditCard size={16} weight="fill" /> : <Check size={16} weight="bold" />}
              {busy ? "Connecting to Stripe…" : (offer.price_aud > 0 ? `Accept & Pay $${offer.price_aud.toFixed(0)} AUD` : "Accept booking")}
              {!busy && <ArrowRight size={14} weight="bold" />}
            </button>
          )}
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}
